import React, { ChangeEvent } from "react";
import {
  TextField,
  MenuItem,
  Select,
  Button,
  CardMedia,
  IconButton,
  Input,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { usePermission } from "../../hooks/usePermission";
import EditText from "../../components/editText";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import { toast } from "react-toastify";
import { RECIPIENT_TYPE, NOTIFICATION_TYPE } from "../../constants/enum";
import { useUploadMediaMutation } from "../../service/common/api";
import { useSendNotificationMutation } from "../../service/features/api";

const AddNotification = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canAdd = hasPermission("Notification Management", "isAdd");

  const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();
  const [sendNotification, { isLoading: isSending }] = useSendNotificationMutation();

  const [title, setTitle] = React.useState("");
  const [type, setType] = React.useState("default");
  const [recipientType, setRecipientType] = React.useState(RECIPIENT_TYPE.CUSTOMERS);
  const [message, setMessage] = React.useState("");
  const [imagePreview, setImagePreview] = React.useState("");
  const [imageFile, setImageFile] = React.useState<File | null>(null);

  const handleTypeChange = (event: any) => {
    setType(event.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title || !message || type === "default") {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      let imageUrl = "";
      if (imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);
        const uploadRes = await uploadMedia(formData).unwrap();
        imageUrl = uploadRes?.data?.url || uploadRes?.url || uploadRes?.data;
      }

      const payload = {
        title,
        message,
        recipientType,
        type,
        image: imageUrl,
      };

      const response = await sendNotification(payload).unwrap();
      toast.success(response.message || "Notification sent successfully");
      navigate("/manage-notifications");
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to send notification");
    }
  };

  return (
    <>
      <CommonBreadcrumb
        title="Add Notification"
        breadcrumbItems={[
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Notification Management", onClick: () => navigate("/manage-notifications") },
          { label: "Add Notification" },
        ]}
      />

      <div className="cards">
        <form className="form" onSubmit={handleSubmit}>
          <div className="gap_p">
            {/* Upload Image */}
            <div className="control_group w_100">
              <label htmlFor="upload">Upload Image (optional)</label>
              {imagePreview ? (
                <div className="upload_image no_radius">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={imagePreview} alt="Uploaded" />
                    </figure>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setImagePreview("");
                        setImageFile(null);
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
              ) : (
                <label className="upload_image no_radius" htmlFor="upload">
                  <Input
                    sx={{ display: "none" }}
                    id="upload"
                    type="file"
                    inputProps={{ accept: "image/png,image/jpeg" }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const files = event.target.files;
                      if (files && files[0] && files[0].type.includes("image")) {
                        setImageFile(files[0]);
                        setImagePreview(URL.createObjectURL(files[0]));
                      }
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img src="/static/images/placeholder.png" alt="icon" />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>

            {/* Notification Title */}
            <div className="control_group w_50">
              <label>Notification Title</label>
              <TextField
                placeholder="Enter Notification Title"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {/* Notification Type */}
            <div className="control_group w_50">
              <label>Type</label>
              <Select fullWidth value={type} onChange={handleTypeChange}>
                <MenuItem disabled value="default">
                  Select Type
                </MenuItem>
                <MenuItem value={NOTIFICATION_TYPE.BROADCAST}>Broadcast</MenuItem>
                <MenuItem value={NOTIFICATION_TYPE.EMAIL}>Email</MenuItem>
                <MenuItem value={NOTIFICATION_TYPE.PUSH}>Push Notification</MenuItem>
              </Select>
            </div>

            {/* Notification Message */}
            <div className="control_group w_100">
              <label>Notification Message</label>
              <EditText content={message} setContent={setMessage} />
            </div>
          </div>

          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary"
              disabled={!canAdd || isUploading || isSending}
            >
              {isUploading ? "Uploading..." : isSending ? "Sending..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddNotification;
